import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getRetention, getGeo, parseReport } from '../api';
import ChartCard from '../components/ChartCard';
import ChartTooltip from '../components/ChartTooltip';

const COLORS = ['#38bdf8', '#818cf8', '#34d399', '#f472b6', '#fb923c', '#fbbf24'];

const RPLABEL = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * R);
  const y = cy + r * Math.sin(-midAngle * R);
  if (percent < 0.05) return null;
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>{(percent * 100).toFixed(0)}%</text>;
};

export default function Audience({ propertyId, startDate, endDate }) {
  const [retention, setRetention] = useState([]);
  const [geo, setGeo] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        getRetention(propertyId, startDate, endDate),
        getGeo(propertyId, startDate, endDate),
      ]);
      setRetention(parseReport(r1.data));
      setGeo(parseReport(r2.data).sort((a, b) => b.sessions - a.sessions).slice(0, 15));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, color: 'var(--text3)', textAlign: 'center' }}>Loading audience data...</div>;

  const retPie = retention.map(r => ({ name: r.newVsReturning === 'new' ? 'New Users' : 'Returning', value: r.activeUsers }));

  return (
    <div style={styles.grid}>
      <ChartCard title="New vs Returning Users" subtitle="User loyalty breakdown">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={retPie} cx="50%" cy="50%" innerRadius={70} outerRadius={120} dataKey="value" labelLine={false} label={RPLABEL}>
              {retPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Retention Stats" subtitle="Detailed breakdown">
        <div style={styles.table}>
          <div style={styles.thead}><span>Type</span><span>Users</span><span>Sessions</span></div>
          {retention.map((r, i) => (
            <div key={i} style={styles.trow}>
              <span style={{ color: COLORS[i], fontWeight: 600, textTransform: 'capitalize' }}>{r.newVsReturning}</span>
              <span>{r.activeUsers?.toLocaleString()}</span>
              <span>{r.sessions?.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </ChartCard>

      <ChartCard title="Top Countries by Sessions" subtitle="Geographic distribution" span={2}>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={geo} layout="vertical" margin={{ top: 0, right: 20, left: 60, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <YAxis type="category" dataKey="country" tick={{ fill: 'var(--text2)', fontSize: 11 }} width={55} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="sessions" name="Sessions" radius={[0, 4, 4, 0]}>
              {geo.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Users by Country" subtitle="Active users per country" span={2}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={geo.slice(0, 10)} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="country" tick={{ fill: 'var(--text3)', fontSize: 10, angle: -30, textAnchor: 'end' }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="activeUsers" name="Active Users" fill="#38bdf8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="newUsers"    name="New Users"    fill="#818cf8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

const styles = {
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  table: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 },
  thead: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '6px 0', fontSize: 10, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' },
  trow:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 0', fontSize: 13, color: 'var(--text2)', borderBottom: '1px solid var(--border2)' },
};
